import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Actions as ACTIONS } from "../../store/actions/leaveActions";
import { format, addMonths, lastDayOfMonth } from "date-fns";
import _ from "lodash";
import {
	NoSymbolIcon,
	ChatBubbleOvalLeftEllipsisIcon,
	CheckIcon,
	PencilSquareIcon,
	PhotoIcon,
} from "@heroicons/react/20/solid";
import Spinner from "../common/Spinner";

const LeaveRow = ({
	leave,
	leaveType,
	setOpenModal,
	noName,
	mode,
	userPermissions,
	saveLeave,
	currentDepartment,
	currentAgreement,
	employee,
	departments,
	isSaving,
	showCancelLeave,
	showComments,
	showAttachments,
	location,
}) => {
	const onChangeLeaveStatus = (status) => {
		leave.status = status;
		saveLeave(leave);
	};

	const sameDay = !leave.dateTo || leave.date == leave.dateTo;

	if (!employee && !leave.employeeName) {
		// this is an employee which doesn't work in this period
		return null;
	}

	const today = new Date();

	return (
		<tr key={leave.id}>
			<td>
				{!isSaving && userPermissions != "Read" && (
					<div
						className="btn btn-xs btn-default"
						title="Edit Leave Request"
						style={{ marginLeft: 10, marginRight: 10 }}
						onClick={() => {
							setOpenModal(true, { ...leave, location });
						}}
					>
						<PencilSquareIcon
							className="h-5 w-5 text-dark-gray"
							style={{ marginTop: "3px" }}
						/>
					</div>
				)}
			</td>
			{location == "leavePage" && (
				<>
					{employee && employee.employeeName ? (
						<td>
							{employee.employeeName} {employee.employeeSurname}
						</td>
					) : (
						<td>
							{leave.employeeName} {leave.employeeSurname}
						</td>
					)}
				</>
			)}
			{employee && (
				<td>
					{departments?.find?.((department) => String(department.id) === String(leave.department))?.name ??
						""}
				</td>
			)}
			<td>
				{(leave.applicationDate && format(new Date(leave.applicationDate), "do MMM yyyy")) ||
					format(new Date(), "do MMM yyyy")}
			</td>
			<td>
				{format(new Date(leave.date), "do MMM yyyy")}
				{` ${leave.startTime}`}
			</td>
			<td>
				{format(new Date(leave.dateTo), "do MMM yyyy")}
				{` ${leave.endTime}`}
			</td>
			<td>{parseFloat(leave.hours).toFixed(2)}</td>
			<td>{leaveType}</td>
			<td>{leave.status}</td>
			<td className="text-center">
				{isSaving && (
					<a
						style={{ marginRight: "5px" }}
						className="btn btn-xs btn-default"
						title="Saving"
					>
						<Spinner
							style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
							className="animate-spin -ml-1 mr-3 h-5 w-5 "
						/>
					</a>
				)}
				{!isSaving && leave.comments && leave.comments.length > 0 && (
					<a
						style={{ marginRight: "5px" }}
						className="btn btn-xs btn-default"
						title="Show Comments"
						onClick={() => {
							showComments(leave);
						}}
					>
						<ChatBubbleOvalLeftEllipsisIcon
							className="h-5 w-5 text-dark-gray"
							style={{ marginTop: "3px" }}
						/>
					</a>
				)}
				{!isSaving && leave.attachments && leave.attachments.length > 0 && (
					<a
						style={{ marginRight: "5px" }}
						className="btn btn-xs btn-default"
						title="Show Comments"
						onClick={() => {
							showAttachments(leave);
						}}
					>
						<PhotoIcon
							className="h-5 w-5 text-dark-gray"
							style={{ marginTop: "3px" }}
						/>
					</a>
				)}
				{!isSaving && userPermissions != "Read" && leave.status == "Pending" && (
					<React.Fragment>
						<a
							style={{ marginRight: "5px" }}
							className="btn btn-xs btn-success"
							title="Approve Leave Request"
							onClick={() => {
								onChangeLeaveStatus("Approved");
							}}
						>
							<CheckIcon
								className="h-5 w-5 text-dark-gray"
								style={{ marginTop: "3px" }}
							/>
						</a>
						<a
							style={{ marginRight: "5px" }}
							className="btn btn-xs btn-danger"
							title="Deny Leave Request"
							onClick={() => {
								showCancelLeave(leave, "Denied");
							}}
						>
							<NoSymbolIcon
								className="h-5 w-5 text-dark-gray"
								style={{ marginTop: "3px" }}
							/>
						</a>
					</React.Fragment>
				)}

				{!isSaving && userPermissions != "Read" && leave.status == "Pending Cancellation" && (
					<React.Fragment>
						<a
							style={{ marginRight: "5px" }}
							className="btn btn-xs btn-success"
							title="Keep Leave"
							onClick={() => {
								onChangeLeaveStatus("Approved");
							}}
						>
							<CheckIcon
								className="h-5 w-5 text-dark-gray"
								style={{ marginTop: "3px" }}
							/>
						</a>
						<a
							style={{ marginRight: "5px" }}
							className="btn btn-xs btn-danger"
							title="Cancel Leave"
							onClick={() => {
								showCancelLeave(leave, "Cancelled");
							}}
						>
							<NoSymbolIcon
								className="h-5 w-5 text-dark-gray"
								style={{ marginTop: "3px" }}
							/>
						</a>
					</React.Fragment>
				)}

				{!userPermissions != "Read" && leave.payPeriodNo && (
					<span>
						{leave.payPeriodFrequency == 12
							? `Paid ${format(new Date(2020, leave.payPeriodNo - 1, 1), "MMM")} ${leave.payPeriodYear}`
							: `Paid ${leave.payPeriodNo} ${leave.payPeriodYear}`}
					</span>
				)}
			</td>
		</tr>
	);
};

const mapStateToProps = (state, ownProps) => {
	const {
		// leave,
		leaveTypes,
	} = state.leave;

	const { leave } = ownProps;

	// TODO update lock date
	const isBeforeLockDate = new Date(leave.start) <= lastDayOfMonth(addMonths(new Date(), -1));

	const userPermissions = state?.permissions?.my?.permissions?.leave;

	const isSaving = state.leave.saving && state.leave.saving[leave.id];

	const {
		// leave,
		employeeListWithSalary,
	} = state.employees;

	const employee =
		employeeListWithSalary && employeeListWithSalary?.find?.((employee) => employee.id == leave.agreement);
	// console.log('employeeForRow',employeeListWithSalary,employee,leave.agreement)

	return {
		leaveType: leaveTypes && leave && _.find(leaveTypes, ({ id }) => id == leave.type)?.name,
		departments: state?.company?.settings?.departments,
		employee,
		userPermissions,
		isSaving,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setOpenModal: (status, leave) => dispatch(ACTIONS.setOpenModal(status, leave)),
	saveLeave: (leave) => dispatch(ACTIONS.saveLeave(leave, true)),
	showCancelLeave: (leave, status) => dispatch(ACTIONS.showCancelLeave(leave, status)),
	showComments: (leave) => dispatch(ACTIONS.showComments(leave)),
	showAttachments: (leave) => dispatch(ACTIONS.showAttachments(leave)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeaveRow);
